import {
  ConversationAvatar,
  ConversationListItemResponse,
  Disclaimer,
  formatRelativeTime,
  lightTheme,
  markdownToPreview,
  TruncatedText,
} from '@vgrubs/components';
import {
  ViktorEntryButton,
  ViktorEntryCount,
  ViktorEntryLine,
  ViktorEntryName,
  ViktorEntryShrink,
  ViktorEntrySlot,
  ViktorEntryText,
} from './ViktorEntryCard.styled';

type Props = {
  /**
   * The viewer's one thread with Viktor, once `POST /v1/conversations/viktor`
   * has answered. Undefined for the moment that takes, during which the card
   * is drawn but cannot open anything.
   */
  thread?: ConversationListItemResponse;
  selected: boolean;
  onClick: () => void;
};

/**
 * The pinned way into Viktor, above the inbox list.
 *
 * A row in everything but position: the thread is not a conversation in the
 * list - it does not scroll, filter or archive with them, and it is the same
 * one thread whichever tab is open - so it sits above the "Messages" title,
 * which names the list Viktor is not in. What tells it apart is the AI
 * gradient, worn by the disc and the name at rest and by the whole card once
 * it is open, the way a selected row goes red.
 *
 * It wears the rows' clothes otherwise: last message and timestamp once a
 * conversation exists, and the unread count when answers landed while the
 * thread was off screen. Viktor writes Markdown, so his last answer is
 * flattened to words for the preview; your own question is cued with "You: ".
 */
export const ViktorEntryCard = ({ thread, selected, onClick }: Props) => {
  /* The rows' quiet ink, and on the open card the same thing translated to
     the gradient: white held back. */
  const quiet = selected ? `${lightTheme.white}B3` : lightTheme.lightGray;

  const preview = thread?.lastMessagePreview
    ? `${
        thread.lastMessageSide === 'internal' ? 'You: ' : ''
      }${markdownToPreview(thread.lastMessagePreview)}`
    : 'Your AI assistant. Ask anything about vGrubs.';

  const unread = thread?.unread ?? 0;

  return (
    <ViktorEntrySlot>
      <ViktorEntryButton
        type="button"
        $selected={selected}
        aria-pressed={selected}
        aria-busy={!thread}
        disabled={!thread}
        onClick={onClick}
      >
        {/* The gradient disc at rest; inverted on the open card, where the
            card is the gradient and the disc goes white with coloured stars -
            the same flip a selected row gives its avatar. */}
        <ConversationAvatar
          type="viktor"
          name="vGrubs AI"
          size={36}
          inverted={selected}
        />
        <ViktorEntryText>
          <ViktorEntryLine>
            <ViktorEntryShrink>
              <ViktorEntryName $selected={selected}>vGrubs AI</ViktorEntryName>
            </ViktorEntryShrink>
            {thread?.lastMessageAt && (
              <Disclaimer $m="0" $color={quiet}>
                {formatRelativeTime(thread.lastMessageAt)}
              </Disclaimer>
            )}
          </ViktorEntryLine>

          <ViktorEntryLine>
            <ViktorEntryShrink>
              <Disclaimer $m="0" $color={quiet}>
                <TruncatedText color={quiet}>{preview}</TruncatedText>
              </Disclaimer>
            </ViktorEntryShrink>
            {unread > 0 && (
              <ViktorEntryCount $selected={selected}>
                {unread > 99 ? '99+' : unread}
              </ViktorEntryCount>
            )}
          </ViktorEntryLine>
        </ViktorEntryText>
      </ViktorEntryButton>
    </ViktorEntrySlot>
  );
};
